"use client"

import { useTour } from "@/hooks/tour/useTour"
import { removeVietnameseChars } from "@/utils/string/removeVietnameseChars"
import { useCallback, useEffect, useMemo, useRef, useState } from "react"
import { useTranslation } from "react-i18next"
import { useAppSelector } from "@/lib/redux/hooks"

type SectionKey =
  | "highlights"
  | "program"
  | "includes"
  | "schedule"
  | "terms"
  | "related"

const STICKY_HEADER_REM = { mobile: 6, desktop: 10.8 }
const SECTION_NAV_REM = 4.6

const getRootFontSize = () =>
  parseFloat(getComputedStyle(document.documentElement).fontSize) || 16

const getStickyTopPx = () => {
  const rem = window.matchMedia("(min-width: 992px)").matches
    ? STICKY_HEADER_REM.desktop
    : STICKY_HEADER_REM.mobile
  return rem * getRootFontSize()
}

const classifyCondition = (title: string): "includes" | "excludes" | "terms" => {
  const normalized = removeVietnameseChars(title).toLowerCase()
  if (normalized.includes("khong bao gom")) return "excludes"
  if (normalized.includes("bao gom")) return "includes"
  return "terms"
}

const SECTION_NAV_ORDER: SectionKey[] = [
  "program",
  "terms",
  "includes",
  "schedule",
  "highlights",
  "related",
]

const TourDetailSectionNav = () => {
  const { tour: { data: tour } } = useTour()
  const { t } = useTranslation()
  const [active, setActive] = useState<SectionKey>("highlights")
  const [isPinned, setIsPinned] = useState(false)
  const [navHeight, setNavHeight] = useState(0)
  const relatedTours = useAppSelector(state => state.tour.rencentlyChoosenTours)
  const wrapRef = useRef<HTMLDivElement>(null)
  const sentinelRef = useRef<HTMLDivElement>(null)

  const sections = useMemo(() => {
    const available = new Map<SectionKey, { key: SectionKey; label: string; id: string }>()
    const highlights = tour?.hightLights || (tour as { highlights?: string })?.highlights
    const conditions = (tour?.contentConditions || []).filter(c => !!c.description)

    if (highlights) {
      available.set("highlights", {
        key: "highlights",
        label: t("tour.highlights"),
        id: "tour-section-highlights",
      })
    }
    if (tour?.itinerary?.length) {
      available.set("program", {
        key: "program",
        label: t("tour.section_nav_itinerary"),
        id: "tour-section-program",
      })
    }
    if (conditions.some(c => classifyCondition(c.title) === "includes")) {
      available.set("includes", {
        key: "includes",
        label: t("tour.section_nav_includes"),
        id: "tour-section-includes",
      })
    }
    if (tour?.departureSchedule?.length) {
      available.set("schedule", {
        key: "schedule",
        label: t("tour.departure_schedule"),
        id: "tour-section-schedule",
      })
    }
    if (conditions.some(c => classifyCondition(c.title) === "terms")) {
      available.set("terms", {
        key: "terms",
        label: t("tour.section_nav_notes"),
        id: "tour-section-terms",
      })
    }
    if (relatedTours.length) {
      available.set("related", {
        key: "related",
        label: t("tour.related_tours"),
        id: "tour-section-related",
      })
    }

    return SECTION_NAV_ORDER
      .map(key => available.get(key))
      .filter((item): item is { key: SectionKey; label: string; id: string } => !!item)
  }, [relatedTours.length, t, tour])

  const syncPinnedLayout = useCallback(() => {
    const wrap = wrapRef.current
    const mainCol = wrap?.closest(".tour-detail__main-col") as HTMLElement | null
    if (!wrap || !mainCol) return

    const colRect = mainCol.getBoundingClientRect()
    const top = getStickyTopPx()
    wrap.style.setProperty("--tour-nav-fixed-top", `${top}px`)
    wrap.style.setProperty("--tour-nav-fixed-left", `${colRect.left}px`)
    wrap.style.setProperty("--tour-nav-fixed-width", `${colRect.width}px`)
    setNavHeight(wrap.offsetHeight)
  }, [])

  const updatePinnedState = useCallback(() => {
    const sentinel = sentinelRef.current
    const wrap = wrapRef.current
    const sectionsEl = wrap?.closest(".tour-detail__sections") as HTMLElement | null
    if (!sentinel || !wrap || !sectionsEl) {
      setIsPinned(false)
      return
    }

    const top = getStickyTopPx()
    const sentinelTop = sentinel.getBoundingClientRect().top
    const sectionsBottom = sectionsEl.getBoundingClientRect().bottom
    const navH = wrap.offsetHeight || navHeight
    const shouldPin = sentinelTop <= top && sectionsBottom > top + navH + 8

    setIsPinned(shouldPin)
    if (shouldPin) syncPinnedLayout()
  }, [navHeight, syncPinnedLayout])

  useEffect(() => {
    if (!sections.length) return
    updatePinnedState()
    window.addEventListener("scroll", updatePinnedState, { passive: true })
    window.addEventListener("resize", updatePinnedState)
    return () => {
      window.removeEventListener("scroll", updatePinnedState)
      window.removeEventListener("resize", updatePinnedState)
    }
  }, [sections.length, updatePinnedState])

  useEffect(() => {
    if (!sections.length) return
    const observer = new IntersectionObserver(
      entries => {
        const visible = entries
          .filter(entry => entry.isIntersecting)
          .sort((a, b) => b.intersectionRatio - a.intersectionRatio)[0]
        if (!visible?.target?.id) return
        const match = sections.find(section => section.id === visible.target.id)
        if (match) setActive(match.key)
      },
      {
        rootMargin: `-${getStickyTopPx() + SECTION_NAV_REM * getRootFontSize()}px 0px -58% 0px`,
        threshold: [0, 0.15, 0.35, 0.55],
      }
    )

    sections.forEach(section => {
      const el = document.getElementById(section.id)
      if (el) observer.observe(el)
    })

    return () => observer.disconnect()
  }, [sections])

  if (sections.length < 2) return null

  return (
    <div className="tour-detail__section-nav-anchor">
      <div ref={sentinelRef} className="tour-detail__section-nav-sentinel" aria-hidden="true" />
      {isPinned && navHeight > 0 && (
        <div
          className="tour-detail__section-nav-spacer"
          style={{ height: navHeight }}
          aria-hidden="true"
        />
      )}
      <div
        ref={wrapRef}
        className={`tour-detail__section-nav-wrap${isPinned ? " tour-detail__section-nav-wrap--pinned" : ""}`}
      >
        <nav className="tour-detail__section-nav" aria-label={t("tour.tour_detail")}>
          {sections.map(section => (
            <button
              key={section.key}
              type="button"
              className={`tour-detail__section-nav-tab${active === section.key ? " tour-detail__section-nav-tab--active" : ""}`}
              onClick={() => {
                setActive(section.key)
                const target = document.getElementById(section.id)
                if (!target) return
                target.scrollIntoView({ behavior: "smooth", block: "start" })
              }}
            >
              {section.label}
            </button>
          ))}
        </nav>
      </div>
    </div>
  )
}

export default TourDetailSectionNav
